/**
 * Datasource Provider Brand Icons
 *
 * Official SVG icons with brand colors for each datasource provider type.
 * Pattern follows llm-config/components/ProviderIcons.tsx
 */

import type { ProviderType } from '../types';

interface ProviderIconProps {
  className?: string;
  size?: number;
}

function GitIcon({ className = '', size = 24 }: ProviderIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"
        fill="#181717"
      />
    </svg>
  );
}

function S3Icon({ className = '', size = 24 }: ProviderIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path d="M12 2L4 6v12l8 4 8-4V6l-8-4z" fill="#E25444" />
      <path d="M12 2l8 4v12l-8 4V2z" fill="#8C3123" />
      <path d="M12 2L4 6l8 4 8-4-8-4z" fill="#F2B0A9" />
      <path
        d="M9.5 11.5l2.5 1v5l-2.5-1v-5zM14.5 11.5l-2.5 1v5l2.5-1v-5z"
        fill="#FFFFFF"
        opacity="0.7"
      />
      <path d="M12 12.5l-2.5-1V9l2.5 1 2.5-1v2.5l-2.5 1z" fill="#FFFFFF" opacity="0.5" />
    </svg>
  );
}

function SharePointIcon({ className = '', size = 24 }: ProviderIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <circle cx="11" cy="9" r="7" fill="#038387" />
      <circle cx="15" cy="14" r="5.5" fill="#1A9BA1" />
      <circle cx="10" cy="16" r="4" fill="#37C6D0" />
      <text
        x="11"
        y="12"
        textAnchor="middle"
        fill="white"
        fontSize="8"
        fontWeight="700"
        fontFamily="Segoe UI, sans-serif"
      >
        S
      </text>
    </svg>
  );
}

function GoogleDriveIcon({ className = '', size = 24 }: ProviderIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path d="M8.627 1.5H15.373L23 15H16.254L8.627 1.5Z" fill="#FFCF63" />
      <path d="M1 15L4.813 21.6H19.187L15.373 15H1Z" fill="#4688F4" />
      <path d="M8.627 1.5L1 15L4.813 21.6L12.44 7.1L8.627 1.5Z" fill="#1EA362" />
      {/* Shadow overlaps for depth */}
      <path d="M12 7.1L8.186 13.7L12 20.3L15.373 15L12 7.1Z" fill="#1EA362" opacity="0.2" />
      <path d="M15.373 15L12 20.3H19.187L15.373 15Z" fill="#FFCF63" opacity="0.2" />
    </svg>
  );
}

function OneDriveIcon({ className = '', size = 24 }: ProviderIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M9.5 18.5h10a3.5 3.5 0 0 0 .5-6.97 5 5 0 0 0-9.2-2.53A4 4 0 0 0 5 13a4 4 0 0 0 4 4h.5z"
        fill="#0364B8"
      />
      <path
        d="M9.5 18.5h10a3.5 3.5 0 0 0 .5-6.97A5 5 0 0 0 14.5 7a5 5 0 0 0-4.7 3.33"
        fill="#0078D4"
      />
      <path
        d="M5 13a4 4 0 0 1 4.8-3.92A5 5 0 0 1 14.5 7a5 5 0 0 1 5 4.53A3.5 3.5 0 0 1 19.5 18.5h-10A4 4 0 0 1 5 13z"
        fill="#0078D4"
        opacity="0.7"
      />
    </svg>
  );
}

function GenericGitIcon({ className = '', size = 24 }: ProviderIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M23.546 10.93L13.067.452a1.55 1.55 0 0 0-2.188 0L8.708 2.627l2.76 2.76a1.838 1.838 0 0 1 2.327 2.341l2.66 2.66a1.838 1.838 0 1 1-1.103 1.03l-2.48-2.48v6.53a1.838 1.838 0 1 1-1.512-.085V8.806a1.838 1.838 0 0 1-.998-2.41L7.62 3.654.454 10.82a1.55 1.55 0 0 0 0 2.189l10.48 10.477a1.55 1.55 0 0 0 2.186 0l10.426-10.37a1.55 1.55 0 0 0 0-2.187z"
        fill="#6B7280"
      />
    </svg>
  );
}

function DefaultDatasourceIcon({ className = '', size = 24 }: ProviderIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <path d="M8 12H16M12 8V16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

const DATASOURCE_ICONS: Record<string, React.FC<ProviderIconProps>> = {
  git: GitIcon,
  s3: S3Icon,
  sharepoint: SharePointIcon,
  google_drive: GoogleDriveIcon,
  onedrive: OneDriveIcon,
  generic_git: GenericGitIcon,
};

export function DatasourceProviderIcon({
  providerType,
  className = '',
  size = 24,
}: ProviderIconProps & { providerType: ProviderType | string }) {
  const IconComponent = DATASOURCE_ICONS[providerType] || DefaultDatasourceIcon;
  return <IconComponent className={className} size={size} />;
}
